/**
 * Decorator that instruments methods with analytics tracking.
 * Works with any class that has an `instrumentMethod` method.
 * Analytics are logged once per instance and method.
 *
 * @param options.name - Custom method name for analytics (defaults to property key)
 * @param options.redact - Keys of the first argument whose string values are replaced in analytics
 */
export const instrumentSdkMethod = (
  options: { name?: string; redact?: string[] } = {},
): MethodDecorator => {
  // WeakSet tracks per-instance rather than per-class-definition, since the
  // decorator closure is shared across all instances of the class.
  // This doesn't occur in production, however is useful for test isolation.
  const loggedInstances = new WeakSet<object>();

  return (_target, propertyKey, descriptor) => {
    const originalMethod = descriptor.value;

    if (typeof originalMethod !== "function") {
      return;
    }

    const methodImplementation = originalMethod as (
      ...args: unknown[]
    ) => unknown;

    type Instrumentable = {
      instrumentMethod: <T>(opts: {
        method: string;
        args: unknown;
        fn: () => T;
        skipAnalytics?: boolean;
      }) => T;
    };

    descriptor.value = function (this: Instrumentable, ...args: unknown[]) {
      const methodName =
        options.name ??
        (typeof propertyKey === "symbol"
          ? (propertyKey.description ?? propertyKey.toString())
          : propertyKey);

      const shouldSkipAnalytics = loggedInstances.has(this);

      loggedInstances.add(this);

      return this.instrumentMethod({
        method: methodName,
        args: redactArgs(args, options.redact),
        fn: () => methodImplementation.apply(this, args),
        skipAnalytics: shouldSkipAnalytics,
      });
    } as typeof originalMethod;
  };
};

function redactArgs(args: unknown[], keys: string[] | undefined): unknown[] {
  const [first, ...rest] = args;
  if (!keys?.length || typeof first !== "object" || first === null) {
    return args;
  }
  const source = first as Record<string, unknown>;
  const redacted = keys.filter((key) => typeof source[key] === "string");
  if (redacted.length === 0) {
    return args;
  }
  return [
    {
      ...source,
      ...Object.fromEntries(redacted.map((key) => [key, "[redacted]"])),
    },
    ...rest,
  ];
}
